import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PersonIcon from "@mui/icons-material/Person";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { Box, Button, Grid, styled, Switch, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { useNotification } from "../../context/NotificationContext";
import {
  BookingDetails,
  bookingService,
} from "../../services/api/bookingService";
import { verificationService } from "../../services/api/verificationService";
import api from "../../services/api/axiosConfig";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PageContainer = styled("div")({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "rgba(0,0,0,0.7)",
  width: "100%",
  margin: 0,
  padding: 0,
  overflow: "hidden",
});

const HeroSection = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundImage: "url(images/background.jpg)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  minHeight: "100vh",
  flex: 1,
  marginBottom: -1,
});

const MainContent = styled("div")(({ theme }) => ({
  width: "100%",
  padding: theme.breakpoints.down("md") ? "0 16px" : "0 20px",
  maxWidth: 1200,
  margin: "0 auto",
  marginTop: theme.breakpoints.up("md")
    ? "150px"
    : theme.breakpoints.down("sm")
    ? "80px"
    : "100px",
  display: "flex",
  flexDirection: "column",
  gap: theme.breakpoints.down("md") ? "24px" : "32px",
}));

const BlueBanner = styled("div")(({ theme }) => ({
  width: "100%",
  background: "#2563EB",
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.breakpoints.up("md")
    ? "48px 0 24px 0"
    : theme.breakpoints.down("sm")
    ? "24px 0 12px 0"
    : "32px 0 16px 0",
  borderRadius: theme.breakpoints.down("md") ? "12px" : "16px",
  marginBottom: theme.breakpoints.down("md") ? "24px" : "32px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
}));

const BannerIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: theme.breakpoints.up("md")
    ? 56
    : theme.breakpoints.down("sm")
    ? 32
    : 40,
  marginBottom: theme.breakpoints.down("md") ? 8 : 12,
}));

const BannerTitle = styled("div")(({ theme }) => ({
  fontSize: theme.breakpoints.up("md")
    ? 36
    : theme.breakpoints.down("sm")
    ? 24
    : 28,
  fontWeight: 700,
  marginBottom: theme.breakpoints.down("md") ? 6 : 8,
  textAlign: "center",
}));

const BannerSubtitle = styled("div")(({ theme }) => ({
  color: "#e0e7ef",
  marginBottom: 0,
  textAlign: "center",
  [theme.breakpoints.up("md")]: {
    fontSize: 16,
  },
  [theme.breakpoints.down("md")]: {
    fontSize: 14,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 12,
  },
}));

const AccessCard = styled("div")(({ theme }) => ({
  background: "#232933",
  borderRadius: theme.breakpoints.down("md") ? 16 : 20,
  boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
  padding: theme.breakpoints.down("md") ? "24px 16px" : "32px",
  color: "#fff",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: "20px 12px",
  },
}));

const AccessTitle = styled("h3")(({ theme }) => ({
  fontSize: theme.breakpoints.down("md") ? 20 : 24,
  fontWeight: 600,
  marginBottom: theme.breakpoints.down("md") ? 16 : 20,
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: 18,
    marginBottom: 12,
  },
}));

const AccessGrid = styled(Grid)(({ theme }) => ({
  gap: theme.breakpoints.down("md") ? 16 : 24,
  [theme.breakpoints.down("sm")]: {
    gap: 12,
  },
}));

const AccessItem = styled("div")(({ theme }) => ({
  background: "#181D23",
  borderRadius: theme.breakpoints.down("md") ? 12 : 16,
  padding: theme.breakpoints.down("md") ? "16px 12px" : "20px 16px",
  display: "flex",
  alignItems: "center",
  gap: theme.breakpoints.down("md") ? 12 : 16,
  [theme.breakpoints.down("sm")]: {
    padding: "12px 8px",
    gap: 8,
  },
}));

const AccessIcon = styled("div")(({ theme }) => ({
  background: "#2563EB",
  borderRadius: "50%",
  width: theme.breakpoints.down("md") ? 40 : 48,
  height: theme.breakpoints.down("md") ? 40 : 48,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    width: 36,
    height: 36,
  },
}));

const AccessText = styled("div")(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
  },
}));

const AccessLabel = styled("div")(({ theme }) => ({
  fontWeight: 600,
  fontSize: theme.breakpoints.down("md") ? 14 : 16,
  marginBottom: theme.breakpoints.down("md") ? 2 : 4,
  [theme.breakpoints.down("sm")]: {
    fontSize: 13,
  },
}));

const AccessValue = styled("div")(({ theme }) => ({
  color: "#b0b8c1",
  fontSize: theme.breakpoints.down("md") ? 12 : 14,
  [theme.breakpoints.down("sm")]: {
    fontSize: 11,
  },
}));

const QrCodeContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: theme.breakpoints.down("md") ? 16 : 24,
  [theme.breakpoints.down("sm")]: {
    marginTop: 12,
  },
}));

const QrCode = styled("div")(({ theme }) => ({
  background: "#fff",
  borderRadius: theme.breakpoints.down("md") ? 12 : 16,
  padding: theme.breakpoints.down("md") ? 16 : 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    padding: 12,
  },
}));

const ActionButtons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.breakpoints.down("md") ? 12 : 16,
  marginTop: theme.breakpoints.down("md") ? 20 : 24,
  flexDirection: theme.breakpoints.down("sm") ? "column" : "row",
  [theme.breakpoints.down("sm")]: {
    gap: 8,
    marginTop: 16,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1,
  background: "#2563EB",
  color: "#fff",
  borderRadius: 8,
  padding: theme.breakpoints.down("md") ? "10px 16px" : "12px 24px",
  fontWeight: 600,
  fontSize: theme.breakpoints.down("md") ? 14 : 16,
  textTransform: "none",
  "&:hover": {
    background: "#1d4ed8",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 13,
    padding: "8px 12px",
  },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
  background: "#232933",
  "&:hover": {
    background: "#2d3748",
  },
}));

const PinBox = styled("div")(({ theme }) => ({
  display: "flex",
  gap: 8,
  background: "#181D23",
  borderRadius: 12,
  padding: theme.breakpoints.up("md") ? "85px 54px" : "40px 20px",
  fontSize: theme.breakpoints.up("md") ? 20 : 14,
  fontWeight: 700,
  letterSpacing: 4,
  // marginBottom: 8,
  flexWrap: "wrap",
  justifyContent: "center",
}));

const PinDigit = styled("span")(({ theme }) => ({
  background: "#232933",
  borderRadius: 6,
  padding: theme.breakpoints.up("md") ? "6px 12px" : "4px 8px",
  fontSize: theme.breakpoints.up("md") ? 20 : 14,
  fontWeight: 600,
}));

const PinDivider = styled("span")(({ theme }) => ({
  fontSize: theme.breakpoints.up("md") ? 20 : 14,
  fontWeight: 600,
  color: "#60a5fa",
  margin: "0 4px",
}));

const DetailsCard = styled("div")(({ theme }) => ({
  background: "#232933",
  borderRadius: 20,
  boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
  padding: theme.breakpoints.up("md") ? 32 : 24,
  color: "#fff",
  marginBottom: 32,
}));

const DetailsLabel = styled("div")(({ theme }) => ({
  color: "#b0b8c1",
  fontSize: theme.breakpoints.up("md") ? 15 : 13,
  marginBottom: 4,
}));

const DetailsValue = styled("div")(({ theme }) => ({
  color: "#fff",
  fontSize: theme.breakpoints.up("md") ? 16 : 14,
  fontWeight: 500,
  background: "#181D23",
  borderRadius: 8,
  padding: "10px 16px",
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

const OverstayCard = styled("div")(({ theme }) => ({
  background: "#232933",
  borderRadius: 20,
  boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
  padding: theme.breakpoints.up("md") ? 32 : 24,
  color: "#fff",
  marginBottom: 32,
}));

const OverstayRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
});

const ActionsRow = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 20,
  marginTop: 32,
  marginBottom: 32,
});

const BookingConfirmationPage = () => {
  const [overstayNotify, setOverstayNotify] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [pin, setPin] = useState<string>("");
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const emailSentRef = useRef(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        const bookingId = sessionStorage.getItem("bookingId");
        if (!bookingId) {
          navigate("/");
          return;
        }

        const data = await bookingService.getBookingById(bookingId);
        setBookingDetails(data);

        // Try to get PIN if identity is verified
        if (data.identity_verified) {
          try {
            const pinData = await verificationService.getBookingPin(bookingId);
            setPin(pinData.pin);
          } catch (error) {
            console.error("Failed to get PIN:", error);
            // Generate fallback PIN if backend fails
            // setPin(generatePin());
          }
        } 
      } catch (error) {
        console.error("Error fetching booking details:", error);
        alert("Failed to load booking details");
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [navigate]);

  // Handle extension payment completion - update booking with PATCH API
  useEffect(() => {
    const handleExtensionPayment = async () => {
      const extensionDataStr = sessionStorage.getItem('extensionData');
      if (!extensionDataStr || !bookingDetails) {
        return;
      }

      // Use a ref to prevent duplicate processing
      const processedKey = `extensionProcessed_${bookingDetails.id}`;
      if (sessionStorage.getItem(processedKey)) {
        return;
      }

      try {
        const extensionData = JSON.parse(extensionDataStr);
        if (!extensionData.isExtension) {
          return;
        }

        // Mark as processing to prevent duplicate calls
        sessionStorage.setItem(processedKey, 'true');

        // Get current booking details
        const currentBooking = await bookingService.getBookingById(bookingDetails.id);
        
        // Calculate new total amount (add extension amount to previous amount)
        const newTotalAmount = Number(currentBooking.total_amount) + extensionData.extensionAmount;
        
        // Calculate new check-out time (add extended hours)
        const currentCheckOut = new Date(currentBooking.check_out);
        const extendedCheckOut = new Date(currentCheckOut.getTime() + (extensionData.extensionHours * 60 * 60 * 1000));
        
        // Generate new PIN
        let newPin = '';
        try {
          // If identity is already verified, send PIN email only (not ID verification email)
          if (currentBooking.identity_verified) {
            // Send PIN email only using the new endpoint
            await api.post('/verification/send-pin-email', {
              bookingId: bookingDetails.id
            });
            
            // Get the newly generated PIN
            const pinResponse = await api.get(`/verification/booking/${bookingDetails.id}/pin`);
            newPin = pinResponse.data?.pin || '';
          } else {
            // If identity not verified, send verification email which generates and sends PIN
            await api.post('/verification/send-verification-email', {
              bookingId: bookingDetails.id
            });
            
            // Get the newly generated PIN
            const pinResponse = await api.get(`/verification/booking/${bookingDetails.id}/pin`);
            newPin = pinResponse.data?.pin || '';
          }
        } catch (pinError) {
          console.error('Failed to generate new PIN:', pinError);
        }
        
        // Prepare special_requests with new PIN
        const currentSpecialRequests = currentBooking.special_requests || '';
        const pinLine = newPin ? `Access PIN: ${newPin}` : '';
        const updatedSpecialRequests = currentSpecialRequests 
          ? `${currentSpecialRequests}\n${pinLine}`.trim()
          : pinLine;
        
        // Call PATCH API to update booking
        const patchData: any = {
          totalAmount: newTotalAmount,
          checkOut: extendedCheckOut.toISOString(),
          payment_status: 'completed',
          special_requests: updatedSpecialRequests
        };
        
        await api.patch(`/bookings/${bookingDetails.id}`, patchData);
        
        // Refresh booking details
        const updatedBooking = await bookingService.getBookingById(bookingDetails.id);
        setBookingDetails(updatedBooking);
        
        // Clear all session storage values after payment process is complete
        sessionStorage.removeItem('extensionData');
        sessionStorage.removeItem('bookingDetails');
        sessionStorage.removeItem('bookingId');
        sessionStorage.removeItem(processedKey);
        
        showNotification('Stay extension completed successfully!', 'success');
      } catch (error: any) {
        console.error('Error updating booking after extension payment:', error);
        showNotification('Failed to complete extension. Please contact support.', 'error');
        // Remove processing flag on error so it can be retried
        if (bookingDetails) {
          const processedKey = `extensionProcessed_${bookingDetails.id}`;
          sessionStorage.removeItem(processedKey);
        }
      }
    };

    if (bookingDetails) {
      handleExtensionPayment();
    }
  }, [bookingDetails, showNotification]);

  // Send verification email when bookingDetails is available
  useEffect(() => {
    const sendVerificationEmail = async () => {
      // Prevent duplicate calls using ref
      if (emailSentRef.current) {
        return;
      }

      if (!bookingDetails || !bookingDetails.id || bookingDetails.identity_verified) {
        return;
      }

      // Set flag immediately to prevent duplicate calls
      emailSentRef.current = true;

      try {
        const response = await api.post('/verification/send-verification-email', {
          bookingId: bookingDetails.id
        });

        if (response.status === 200 || response.status === 201) {
          setVerificationEmailSent(true);
          showNotification("Verification email sent successfully", "success");
        } else {
          // Reset flag if request failed
          emailSentRef.current = false;
        }
      } catch (error: any) {
        console.error("Error sending verification email:", error);
        // Reset flag on error so it can be retried if needed
        emailSentRef.current = false;
      }
    };

    sendVerificationEmail();
  }, [bookingDetails?.id, bookingDetails?.identity_verified, showNotification]);

  // Clear session storage for regular bookings (non-extension) after page loads
  useEffect(() => {
    if (bookingDetails && !loading) {
      const extensionData = sessionStorage.getItem('extensionData');
      // If it's not an extension payment, clear session storage after a delay
      // to allow the page to fully load
      if (!extensionData) {
        const timer = setTimeout(() => {
          sessionStorage.removeItem('bookingDetails');
          sessionStorage.removeItem('bookingId');
        }, 2000); // Clear after 2 seconds to ensure page is loaded

        return () => clearTimeout(timer);
      }
    }
  }, [bookingDetails, loading]);

  // Generate a random PIN for demo purposes
  const generatePin = () => {
    return Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join(
      ""
    );
  };

  const handleExtendStay = async () => {
    // Implement extend stay logic
    alert("Extend stay functionality coming soon!");
  };

  const handleCancelBooking = async () => {
    if (!bookingDetails) return;

    if (window.confirm("Are you sure you want to cancel this booking?")) {
      try {
        const response = await fetch(
          `/api/bookings/${bookingDetails.id}/cancel`,
          {
            method: "POST",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to cancel booking");
        }

        alert("Booking cancelled successfully");
        navigate("/");
      } catch (error) {
        console.error("Error cancelling booking:", error);
        alert("Failed to cancel booking");
      }
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <HeroSection>
          <Header />
          <MainContent>
            <Typography
              variant="h2"
              sx={{ textAlign: "center", color: "#fff" }}
            >
              Loading Booking Details...
            </Typography>
          </MainContent>
        </HeroSection>
        <Footer />
      </PageContainer>
    );
  }

  if (!bookingDetails) {
    return (
      <PageContainer>
        <HeroSection>
          <Header />
          <MainContent>
            <Typography
              variant="h2"
              sx={{ textAlign: "center", color: "#fff" }}
            >
              Booking Not Found
            </Typography>
          </MainContent>
        </HeroSection>
        <Footer />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeroSection>
        <Header />
        <MainContent>
          <BlueBanner>
            <BannerIcon />
            <BannerTitle>
              {bookingDetails.identity_verified
                ? "Booking Confirmed!"
                : "Payment Successful!"}
            </BannerTitle>
            <BannerSubtitle>
              {bookingDetails.identity_verified
                ? "Your Stay Is Successfully Booked"
                : "Complete ID verification to access your botel"}
            </BannerSubtitle>
          </BlueBanner>

          <Grid container spacing={4} justifyContent="center">
            <Grid size={6}>
              <AccessCard sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: 'inherit',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    backdropFilter: 'blur(2px)',
                    pointerEvents: 'none'
                  }}
                >
                  <Typography
                    sx={{
                      color: '#9ca3af',
                      fontSize: { xs: 20, md: 24 },
                      fontWeight: 'bold',
                      mb: 1,
                      textAlign: 'center'
                    }}
                  >
                    Coming Soon
                  </Typography>
                  <Typography
                    sx={{
                      color: '#6b7280',
                      fontSize: { xs: 14, md: 16 },
                      textAlign: 'center',
                      px: 2
                    }}
                  >
                    This feature will be available in a future release
                  </Typography>
                </Box>
                <AccessTitle>Scan To Access</AccessTitle>
                <QrCodeContainer>
                  <QrCode>
                    <QrCode2Icon
                      sx={{ fontSize: { xs: 150, md: 200 }, mb: 1 }}
                    />
                  </QrCode>
                </QrCodeContainer>
                <Typography
                  sx={{
                    color: "#b0b8c1",
                    fontSize: { xs: 13, md: 15 },
                    textAlign: "center",
                  }}
                >
                  {/* http://qrcodescan-botelbybellhop/{bookingDetails.id} */}
                </Typography>
              </AccessCard>
            </Grid>
            <Grid size={6}>
              <AccessCard>
                <AccessTitle>Smart Lock PIN</AccessTitle>
                {bookingDetails.identity_verified ? (
                  <>
                    <PinBox>
                      {pin.split("").map((digit, index) => (
                        <React.Fragment key={index}>
                          <PinDigit>{digit}</PinDigit>
                          {index === 3 && <PinDivider>-</PinDivider>}
                        </React.Fragment>
                      ))}
                    </PinBox>
                    <Typography
                      sx={{
                        color: "#b0b8c1",
                        fontSize: { xs: 13, md: 15 },
                        textAlign: "center",
                        py:"17.5px"
                      }}
                    >
                      Enter This Code On The Door Keypad
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        background: "#181D23",
                        borderRadius: 12,
                        padding: { xs: "40px 20px", md: "85px 54px" },
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#b0b8c1",
                          fontSize: { xs: 14, md: 16 },
                          mb: 2,
                        }}
                      >
                        PIN will be sent after ID verification
                      </Typography>
                      <Typography
                        sx={{ color: "#60a5fa", fontSize: { xs: 12, md: 14 } }}
                      >
                        Check your email for verification link
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        color: "#b0b8c1",
                        fontSize: { xs: 13, md: 15 },
                        textAlign: "center",
                        mt: 2,
                      }}
                    >
                      Complete ID verification to receive your access PIN
                    </Typography>
                  </>
                )}
              </AccessCard>
            </Grid>
          </Grid>

          <DetailsCard>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Booking Details
            </Typography>
            <Grid container spacing={3}>
              <Grid size={6}>
                <DetailsLabel>Guest Name</DetailsLabel>
                <DetailsValue>
                  <PersonIcon sx={{ fontSize: 20 }} />{" "}
                  {bookingDetails.guest.email}
                </DetailsValue>
              </Grid>
              <Grid size={6}>
                <DetailsLabel>Booking ID</DetailsLabel>
                <DetailsValue>
                  <CalendarMonthIcon sx={{ fontSize: 20 }} /> #
                  {bookingDetails.id.slice(0, 8)}
                </DetailsValue>
              </Grid>
              <Grid size={6}>
                <DetailsLabel>Phone Number</DetailsLabel>
                <DetailsValue>
                  <BusinessCenterIcon sx={{ fontSize: 20 }} />{" "}
                  {bookingDetails.guest.phone}
                </DetailsValue>
              </Grid>
              <Grid size={6}>
                <DetailsLabel>Total Amount</DetailsLabel>
                <DetailsValue>
                  <MeetingRoomIcon sx={{ fontSize: 20 }} /> $
                  {Number(bookingDetails?.total_amount || 0).toFixed(2)}
                </DetailsValue>
              </Grid>
              <Grid size={6}>
                <DetailsLabel>Check-In</DetailsLabel>
                <DetailsValue>
                  <CalendarMonthIcon sx={{ fontSize: 20 }} />{" "}
                  {new Date(bookingDetails.check_in).toLocaleDateString()} -
                  3:00 PM
                </DetailsValue>
              </Grid>
              <Grid size={6}>
                <DetailsLabel>Check-Out</DetailsLabel>
                <DetailsValue>
                  <CalendarMonthIcon sx={{ fontSize: 20 }} />{" "}
                  {new Date(bookingDetails.check_out).toLocaleDateString()} -
                  10:00 AM
                </DetailsValue>
              </Grid>
            </Grid>
          </DetailsCard>

          <OverstayCard>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Overstay Tracking
            </Typography>
            <OverstayRow>
              <span>Receive Notifications About Check-Out Time</span>
              <Switch
                checked={overstayNotify}
                onChange={(_, checked) => setOverstayNotify(checked)}
                color="primary"
                inputProps={{ "aria-label": "Overstay Notification Toggle" }}
              />
            </OverstayRow>
          </OverstayCard>

          <ActionsRow>
            <ActionButton
              sx={{
                backgroundColor: "#2563EB",
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
              variant="contained"
              startIcon={<LockIcon />}
              onClick={() => navigate("/client/dashboard")}
            >
              Go To Dashboard
            </ActionButton>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: "100%",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              {/* <ActionButton
                sx={{
                  backgroundColor: "#22c55e",
                  "&:hover": { backgroundColor: "#16a34a" },
                }}
                variant="contained"
                onClick={handleExtendStay}
              >
                Extend Stay
              </ActionButton> */}
              <ActionButton
                sx={{
                  backgroundColor: "#ef4444",
                  "&:hover": { backgroundColor: "#dc2626" },
                }}
                variant="contained"
                onClick={handleCancelBooking}
              >
                Cancel Booking
              </ActionButton>
            </Box>
          </ActionsRow>
        </MainContent>
      </HeroSection>
      <Footer />
    </PageContainer>
  );
};

export default BookingConfirmationPage;
